// src/components/Menu.js
import React, { useEffect, useState } from 'react';
import api from '../api';
import MenuItem from './MenuItem';
import {
  Grid,
  Container,
  Typography,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  Box
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FreeBreakfastIcon from '@mui/icons-material/FreeBreakfast';
import LunchDiningIcon from '@mui/icons-material/LunchDining';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import KitchenIcon from '@mui/icons-material/Kitchen';
import SpaIcon from '@mui/icons-material/Spa';
import logo from '../assets/logo.png';
import { useCart } from '../CartContext';

// legend icons
import GF from '../assets/gluten_free.png';
import VT from '../assets/Vegetarian.png';
import VG from '../assets/vegan.png';

const subcategoryIcons = {
  Sandwiches: <LunchDiningIcon sx={{ mr: 1 }} />,
  Sides: <FastfoodIcon sx={{ mr: 1 }} />,
  'Made-to-Order': <KitchenIcon sx={{ mr: 1 }} />,
  Salads: <SpaIcon sx={{ mr: 1 }} />,
};

const subcategoryColors = {
  Sandwiches: '#E67E22',
  Sides: '#8B4513',
  'Made-to-Order': '#DAA520',
  Salads: '#6B8E23',
};

export default function Menu() {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const { orderType, setOrderType } = useCart();

  const now = new Date();
  const currentTime = now.getHours() + now.getMinutes() / 60;

  // --- Store-hours helper (Mon–Fri 8:00–15:00, closed 10:31–10:59) ---
  function isStoreOpen() {
    const d = new Date();
    const day = d.getDay(); // 0 Sun, 1 Mon, ... 6 Sat
    if (day === 0 || day === 6) return false; // weekends closed

    const minutes = d.getHours() * 60 + d.getMinutes();
    const openMin  = 8 * 60;   // 08:00
    const closeMin = 15 * 60;  // 15:00 exact close

    if (minutes < openMin || minutes >= closeMin) return false;

    // mid-morning closure: 10:31–10:59
    if (minutes >= (10 * 60 + 31) && minutes <= (10 * 60 + 59)) return false;

    return true;
  }
  // -------------------------------------------------------------------

  useEffect(() => {
    api.get('/menu')
      .then(res => {
        const data = res.data;
        const list = Array.isArray(data)
          ? data
          : (data && Array.isArray(data.items) ? data.items : []);
        setItems(list);
      })
      .catch(() => {
        setItems([]); // keep it safe for .filter()
      })
      .finally(() => setLoading(false));
  }, []);

  const filterItems = (mealType, subCategory) =>
    items.filter(i => i.mealType === mealType && i.subCategory === subCategory);

  const isBreakfast = currentTime >= 8 && currentTime <= 10.30;
  const isLunch     = currentTime >= 11 && currentTime <= 15;

  const breakfastCats = ['Sandwiches','Sides','Made-to-Order'];
  const lunchCats     = ['Sandwiches','Sides','Salads'];

  return (
    <Container sx={{ py: 4 }}>
      {/* Café Header & Legend */}
      <Paper variant="outlined" sx={{ p: 3, mb: 4, borderColor: 'grey.500' }}>
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Box component="img" src={logo} alt="LA Catering" sx={{ height: 40, mb: 1 }} />
          <Typography variant="h4">LA Catering │ Capitol Café</Typography>
          <Typography variant="subtitle2">
            670 Harmon Ave, Columbus, OH 43223 │ 8 AM–3 PM
          </Typography>
        </Box>

        <Box sx={{ borderTop: '1px solid #ccc', pt: 2, mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom><strong>Legend</strong></Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box component="img" src={GF} alt="GF" sx={{ width:24, height:24, mr:1 }} />
              <Typography variant="body2">Gluten-Free</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box component="img" src={VT} alt="Veg" sx={{ width:24, height:24, mr:1 }} />
              <Typography variant="body2">Vegetarian</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box component="img" src={VG} alt="Vgn" sx={{ width:24, height:24, mr:1 }} />
              <Typography variant="body2">Vegan</Typography>
            </Box>
            <Typography variant="body2" sx={{ fontStyle:'italic', ml:2 }}>
              * Gluten-free option on breakfast sandwiches: +$1.00
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Order Type */}
      <FormControl sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
        <RadioGroup row value={orderType} onChange={e => setOrderType(e.target.value)}>
          <FormControlLabel value="Dine-In" control={<Radio />} label="Dine-In" />
          <FormControlLabel value="Take-Out" control={<Radio />} label="Take-Out" />
        </RadioGroup>
      </FormControl>

      {/* Menu Sections */}
      {loading ? (
        <Grid container justifyContent="center"><CircularProgress/></Grid>
      ) : (
        // closed-window message (Mon–Fri 8–3 only, excluding 10:31–10:59)
        !isStoreOpen() ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="h5" color="error">
              Store is currently closed. Please come back during business hours
              (Mon–Fri, 8 AM – 3 PM, and is also closed before lunch between 10:31–10:59 AM).
            </Typography>
          </Box>
        ) : (
          <>
            {isBreakfast && (
              <Accordion defaultExpanded sx={{ mb:2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <FreeBreakfastIcon sx={{ mr:1, color:'#DAA520' }} />
                  <Typography variant="h5" sx={{ color:'#DAA520', fontWeight:'bold' }}>
                    Breakfast (8:00–10:30 AM)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {breakfastCats.map(sub => (
                    <Accordion key={sub} sx={{ mb:1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        {subcategoryIcons[sub]}
                        <Typography variant="h6" sx={{ color:subcategoryColors[sub] }}>
                          {sub}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={3}>
                          {filterItems('Breakfast', sub).map((it,i) => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                              <MenuItem item={it} />
                            </Grid>
                          ))}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </AccordionDetails>
              </Accordion>
            )}

            {isLunch && (
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <LunchDiningIcon sx={{ mr:1, color:'#B22222' }} />
                  <Typography variant="h5" sx={{ color:'#B22222', fontWeight:'bold' }}>
                    Lunch (11:00 AM– 3:00 PM)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {lunchCats.map(sub => (
                    <Accordion key={sub} sx={{ mb:1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        {subcategoryIcons[sub]}
                        <Typography variant="h6" sx={{ color:subcategoryColors[sub] }}>
                          {sub}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={3}>
                          {filterItems('Lunch', sub).map((it,i) => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                              <MenuItem item={it} />
                            </Grid>
                          ))}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </AccordionDetails>
              </Accordion>
            )}
          </>
        )
      )}
    </Container>
  );
}
